import { ApolloClient, gql } from '@apollo/client/core';
import type { FetchPolicy, NormalizedCacheObject } from '@apollo/client/core';
import type { Mutation, Query, SaveEventMutationInput } from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import { CONNECTION_METADATA_FIELDS } from '../../connection/Api';
import type { ConnectionArguments } from '../../connection/Api';
import * as GqlConnection from '../../connection/GqlConnection';
import * as Ordering from '../../Shared/Ordering';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as Event from './Event';
import * as EventDetail from './EventDetail';
import * as EventInput from './EventInput';
import * as EventSalesAggregated from './EventSalesAggregated';
import type { EventSearchArguments } from './EventSearchArguments';
import * as EventSummary from './EventSummary';
import * as EventTicketType from './EventTicketType';
import * as TicketSalesByType from './TicketSalesByType';
import { EVENT_DETAIL_FIELDS, EVENT_LIST_FIELDS, EVENT_LIST_MINIMAL_FIELDS, EVENT_SUMMARY_FRAGMENT, EVENT_TICKET_TYPE_FIELDS } from './fragments';

export const EVENTS_QUERY = gql`
  query Events($after: String, $first: Int, $filter: EventSearchArguments!, $sort: [OrderingArgument!]) {
    events(after: $after, first: $first, filter: $filter, sort: $sort) {
      edges {
        cursor
        node {
          ...EventListFields
        }
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
      totalCount
    }
  }
  ${EVENT_LIST_FIELDS}
  ${CONNECTION_METADATA_FIELDS}
`;

export const EVENTS_MINIMAL_QUERY = gql`
  query EventsMinimal($after: String, $first: Int, $filter: EventSearchArguments!, $sort: [OrderingArgument!]) {
    events(after: $after, first: $first, filter: $filter, sort: $sort) {
      edges {
        cursor
        node {
          ...EventListMinimalFields
        }
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
      totalCount
    }
  }
  ${EVENT_LIST_MINIMAL_FIELDS}
  ${CONNECTION_METADATA_FIELDS}
`;

/**
 * Stand-ins for "no lower/upper date bound". The resolver compacts the filter
 * before reading it, so an explicit `null` reaches it as an absent key and
 * silently applies the default today..+1 year window instead.
 */
export const UNBOUNDED_DATE_FROM = '1900-01-01';
export const UNBOUNDED_DATE_TO = '2999-12-31';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The resolver overlaps `dateFrom...dateTo` as a tsrange with an exclusive upper
 * bound, and — unlike the legacy API, which expanded `endDate` with
 * `.end_of_day` — passes the string through untouched. Without this an event on
 * `dateTo` itself stops matching.
 */
const endOfDay = (date: string): string => DATE_ONLY.test(date) ? `${date}T23:59:59.999` : date;

const toGqlFilter = ({ dateFrom, dateTo, ...rest }: EventSearchArguments) => ({
  ...rest,
  ...(dateFrom === undefined ? {} : { dateFrom: dateFrom ?? UNBOUNDED_DATE_FROM }),
  ...(dateTo === undefined ? {} : { dateTo: endOfDay(dateTo ?? UNBOUNDED_DATE_TO) }),
});

export type EventConnection = GqlConnection.GqlConnection<EventSummary.EventSummary> & {
  totalCount: number;
};

export const fetchEvents = async (
  client: ApolloClient<Query>,
  {
    pagination,
    filter,
    sort,
    minimal,
    fetchPolicy,
  }: {
    pagination?: ConnectionArguments;
    filter?: EventSearchArguments;
    sort?: Ordering.Ordering<'name' | 'date'>[];
    minimal?: boolean;
    /**
     * Defaults to `cache-first`, as the legacy list did: paging back to a
     * visited page should not re-pay `ticketTotals`, which the backend resolves
     * per row through unbatched aggregations. Pass `network-only` where the
     * caller needs current sales figures.
     */
    fetchPolicy?: FetchPolicy;
  } = {},
): Promise<EventConnection> => {
  const { data } = await client.query({
    query: minimal ? EVENTS_MINIMAL_QUERY : EVENTS_QUERY,
    fetchPolicy: fetchPolicy ?? 'cache-first',
    variables: {
      // Forward paging only: the connection drops `last` from its arguments and
      // raises on it, and reports `hasPreviousPage` as false unconditionally.
      after: pagination?.after,
      first: pagination?.first,
      filter: toGqlFilter(filter ?? {}),
      sort: sort ?? [],
    },
  });

  if (!data.events) {
    throw new Error('Failed to fetch events.');
  }

  return {
    ...GqlConnection.fromGql(data.events, EventSummary.fromGql),
    totalCount: data.events.totalCount,
  };
};

/**
 * The AppSchema field is the same `event(id:)`, lifted out of the legacy `app { }` wrapper to
 * the query root. The operation is named `EventDetail` rather than the legacy `getEvent`: no
 * e2e spec aliases it -- unlike `saveEvent` and `SaveAnnounce`, whose lowercase names were kept
 * for exactly that reason -- and the reads beside it here are already PascalCase.
 */
export const EVENT_DETAIL_QUERY = gql`
  query EventDetail($eventId: Int!) {
    event(id: $eventId) {
      ...EventDetailFields
    }
  }
  ${EVENT_DETAIL_FIELDS}
`;

/**
 * `network-only`, as the legacy read was. The Prism connector refetches through this straight
 * after `connectPrismEvent` / `disconnectPrismEvent`, whose payloads write
 * `event { id connectedToPrism prismEventId }` into the same normalized entity on this client;
 * a cached read would answer the connector with the state it just changed.
 */
export const fetchEvent = async (
  client: ApolloClient<Query>,
  eventId: number,
): Promise<EventDetail.EventDetail> => {
  const { data } = await client.query<Query>({
    query: EVENT_DETAIL_QUERY,
    variables: { eventId },
    fetchPolicy: 'network-only',
  });

  if (!data.event) {
    throw new Error('Failed to fetch event.');
  }

  return EventDetail.fromGql(data.event);
};

export const AGGREGATED_EVENT_SALES_QUERY = gql`
  query AggregatedEventSales($filter: EventSearchArguments) {
    aggregatedEventSales(filter: $filter) {
      salesData {
        totalTicketsSalesToday
        totalTicketsSoldToday
        totalTicketsSales
        totalTicketsSold
        totalTicketsAvailable
        totalTickets
        totalTicketsOnHold
        totalSalesLimit
        totalSales
        ticketsSold
      }
    }
  }
`;

/**
 * Shares `toGqlFilter` with the list queries above so the header totals and the rows
 * beneath them always answer for the same date window.
 */
export const fetchAggregatedEventSales = async (
  client: ApolloClient<Query>,
  filter: EventSearchArguments,
): Promise<EventSalesAggregated.EventSalesAggregated> => {
  const { data } = await client.query<Query>({
    query: AGGREGATED_EVENT_SALES_QUERY,
    variables: { filter: toGqlFilter(filter) },
    fetchPolicy: 'network-only',
  });

  return EventSalesAggregated.fromGql(data.aggregatedEventSales);
};

/**
 * The AppSchema field is the same `saveEvent`, lifted out of the legacy `app { }` wrapper to
 * the mutation root — and the *operation* name stays `saveEvent` too: `event-create-modal.cy.ts`
 * aliases the request by it.
 *
 * The payload selection reuses `EventSummaryFields`, so it drops the `state` the legacy
 * mutation asked for. No caller reads it — both modals take only `id` and `name` — and this is
 * the settled `on Event` selection, shared with the order queries.
 */
export const SAVE_EVENT_MUTATION = gql`
  mutation saveEvent($input: SaveEventMutationInput!) {
    saveEvent(input: $input) {
      success
      event {
        ...EventSummaryFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${EVENT_SUMMARY_FRAGMENT}
  ${VP_ERROR_FIELDS}
`;

/**
 * Creates when the input carries no `id`, updates when it does.
 */
export const saveEvent = async (
  client: ApolloClient<Mutation>,
  event: EventInput.EventInput,
): Promise<VPResult<Event.Event, VPErrors.VPError[]>> => {
  const input: SaveEventMutationInput = { event: EventInput.toGqlInput(event) };

  const { data } = await client.mutate<Mutation>({ mutation: SAVE_EVENT_MUTATION, variables: { input } });

  const payload = data?.saveEvent;

  if (!payload) {
    throw new Error('saveEvent mutation returned no data');
  }

  if (payload.success) {
    // Falling through to the error branch here would hand the modal an empty — but truthy —
    // error array, and it would then dereference an absent `data`. Failing at the API
    // boundary keeps the message legible.
    if (!payload.event) {
      throw new Error('saveEvent reported success without an event');
    }

    return { success: true, data: Event.fromGql(payload.event) };
  }

  return { success: false, errors: VPErrors.fromGqlCollection(payload.errors ?? []) };
};

export const TICKET_TYPES_QUERY = gql`
  query TicketTypes($eventId: Int!) {
    ticketTypes(eventId: $eventId) {
      ...EventTicketTypeFields
    }
  }
  ${EVENT_TICKET_TYPE_FIELDS}
`;

/**
 * The ticket types an event offers, for the discount and presale pickers.
 *
 * Two callers with opposite needs, so the policy is the caller's: the *form* reads
 * `network-only`, because the operator creates ticket types on the Tickets tab of the same
 * modal and a cached list would offer a picker missing the ticket they just made; the
 * *expanded read-only rows* read from the cache, because Apollo dedupes only in-flight
 * queries and one row per expansion must not cost one round trip each — nor let two rows
 * disagree on a name.
 *
 * A `null` list throws rather than resolving `[]`. The field is nullable, so a null is a
 * backend fault, and flattening it would render "this event has no ticket types" over a
 * failed read — telling the operator to go create tickets they already have.
 */
export const fetchTicketTypes = async (
  client: ApolloClient<Query>,
  eventId: number,
  fetchPolicy: FetchPolicy = 'cache-first',
): Promise<EventTicketType.EventTicketType[]> => {
  const { data } = await client.query<Query>({
    query: TICKET_TYPES_QUERY,
    fetchPolicy,
    variables: { eventId },
  });

  if (!data.ticketTypes) {
    throw new Error('Failed to fetch ticket types.');
  }

  return data.ticketTypes.map(EventTicketType.fromGql);
};

export const TICKET_SALES_BY_TYPE_QUERY = gql`
  query EventTicketSalesByType($eventId: Int!) {
    event(id: $eventId) {
      id
      ticketSales {
        blueprintId
        numberSold
      }
    }
  }
`;

// A null `ticketSales` is an event nobody has bought from; a null `event` is not found, or not
// visible to this account, and flattening that would print a zero beside a selectable audience.
// `TicketSales` has no id, so a second query selecting more of it would overwrite these rows —
// see the `venueRoom.venue` note in fragments.ts.
export const fetchTicketSalesByType = async (
  client: ApolloClient<NormalizedCacheObject>,
  eventId: number,
  fetchPolicy: FetchPolicy = 'cache-first',
): Promise<TicketSalesByType.TicketSalesByType[]> => {
  const { data } = await client.query<Query>({
    query: TICKET_SALES_BY_TYPE_QUERY,
    fetchPolicy,
    variables: { eventId },
  });

  if (!data.event) {
    throw new Error('Failed to fetch ticket sales.');
  }

  return (data.event.ticketSales ?? []).map(TicketSalesByType.fromGql);
};
